import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { IDropdowns, IFilterDropdownsForm } from '@core/models/dropdowns.model';
import { CodeManagementApiService } from '@core/services/code-management-api.service';
import { catchError, pairwise, startWith } from 'rxjs/operators';
import { of } from 'rxjs';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';

const LOADING_DROPDOWNS: IDropdowns[] = [{ label: 'Loading...', value: '' }];

@Component({
  selector: 'app-filter-dropdowns',
  templateUrl: './filter-dropdowns.component.html',
  styleUrls: ['./filter-dropdowns.component.scss'],
  imports: [ 
    SelectModule, 
    FormsModule, 
    ReactiveFormsModule,
    ButtonModule 
  ],
  standalone: true
})
export class FilterDropdownsComponent implements OnInit {
  @Output() dropdownsEvent = new EventEmitter<IFilterDropdownsForm>();
  
  filterForm = this.fb.group({
    productId: this.fb.control<string>('', [Validators.required]),
    tenantId: this.fb.control<string>('', [Validators.required]),
    overrideTypeId: this.fb.control<string>('', [Validators.required]),
  });
  
  programs: IDropdowns[] = [];
  tenants: IDropdowns[] = [];
  plans: IDropdowns[] = [];
  selectedProgramLabel: string = '';
  selectedPlanLabel: string = '';

  constructor(
    private fb: FormBuilder,
    public codeManagementService: CodeManagementApiService
  ) {}

  ngOnInit(): void {
    this.loadPrograms();

    this.filterForm.valueChanges
      .pipe(
        startWith({
          productId: '',
          tenantId: '',
          overrideTypeId: '',
        }),
        pairwise()
      )
      .subscribe(([prev, next]) => {
        if (prev.productId !== next.productId && next.productId) {
          this.tenants = LOADING_DROPDOWNS;
          this.selectedProgramLabel = this.programs.find(p => p.value === next.productId)?.label || '';
          
          this.codeManagementService
            .getTenants()
            .pipe(catchError((error) => {
              this.tenants = [];
              return of([]);
            }))
            .subscribe(data => {
              this.tenants = data.map(item => ({
                label: item.name,
                value: item.tenantId,
              }));
            });
          this.plans = [];
          this.filterForm.patchValue({ tenantId: '', overrideTypeId: '' }, { emitEvent: false });
        } 
        else if (
          prev.tenantId !== next.tenantId &&
          next.tenantId &&
          next.productId
        ) {
          this.plans = LOADING_DROPDOWNS;
          
          this.codeManagementService
            .getPlans(next.tenantId, next.productId)
            .pipe(catchError((error) => {
              this.plans = [];
              return of([]);
            }))
            .subscribe(data => {
              this.plans = data.map(item => ({
                label: item.overrideApplicationKey,
                value: item.overrideTypeId,
              }));
            });
          this.filterForm.patchValue({ overrideTypeId: '' }, { emitEvent: false });
        }
        
        if (
          prev.overrideTypeId !== next.overrideTypeId &&
          this.filterForm.valid
        ) {
          this.selectedPlanLabel = this.plans.find(p => p.value === next.overrideTypeId)?.label || '';
          this.dropdownsEvent.emit({
            tenantId: next.tenantId,
            productId: next.productId,
            productLabel: this.selectedProgramLabel,
            overrideTypeId: next.overrideTypeId,
            overrideTypeLabel: this.selectedPlanLabel
          } as IFilterDropdownsForm);
        }
      });
  }

  private loadPrograms(): void {
    this.programs = LOADING_DROPDOWNS;
    this.codeManagementService
      .getAllProducts()
      .pipe(catchError((error) => {
        this.programs = [];
        return of([]);
      }))
      .subscribe(data => {
        this.programs = data.map(item => ({
          label: item.shortName,
          value: item.productId,
        }));
      });
  }
}
